import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Grid, MenuItem, Paper } from '@mui/material';
import { Store } from "@mui/icons-material";
import { isEmpty, isEqual } from "lodash";
import { showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { getAdminAccessLevel, getUserId, updateUserInformation } from "../../../actions/profile/UserActions";
import { getSiteByIdForUser, getUserByLoginIdForAdmin, updateSiteInformation } from "../../../actions/internal/SiteActions";
import { isValidEmail } from "../../../utilities/Validation";
import { getClockifyClients, getClockifyProjects } from "../../../clients/clockify/ClockifyApi";
import { COMPANY_TYPES } from "../../../constants/Common";
import UpdateSiteImageModal from "../modals/UpdateSiteImageModal";
import { SITE_STATUSES } from "../../../constants/Statuses";
import StyledSubHeader from "../../common/StyledSubHeader";
import { StyledMultilineTextField, StyledTextField } from "../../common/StyledTextField";
import { getSiteById } from "../../../selectors/SiteSelectors";
import StyledProgressCircle from "../../common/StyledProgressCircle";
import { LoadingButton, TextButton } from "../../common/StyledButtons";
import { withRouter } from "react-router-dom";
import { observer } from "mobx-react-lite";
import StyledSwitch from "../../common/StyledSwitch";
import { getUserByLoginId } from "../../../selectors/UsersSelectors";
import { StyledDropDown } from "../../common/StyledDropDown";
import StyledPage from "../../common/StyledPage";

const AdminEditUserAccountPage = ({ classes, history, users, match, location }) => {

    const userLoginId = useRef();
    const originalFields = useRef();

    const selectableClockifyProjects = useRef([])
    const selectableClockifyClients = useRef([])

    const [ showEditAccessModal, setShowEditAccessModal ] = useState(false)
    const [ fields, setFields ] = useState();

    const [showAddSiteImage, setShowAddSiteImage] = useState(false);

    const [ errors, setErrors ] = useState([]);
    const [ isLoading, setIsLoading ] = useState(true);
  
    const user = getUserByLoginId(userLoginId.current);
    const isSuperUser = getAdminAccessLevel() === 100;

    // const site = getSiteById(siteId)

    useEffect(() => {
        loadData().then();
    }, [])

    const prepareFields = (user) => ({
        firstName: user.user_personal.first_name,
        lastName: user.user_personal.last_name,
        email: user.user_contact.email ? user.user_contact.email.primary : null,
        phone: user.user_contact.phone ? user.user_contact.phone.primary : null,
        promotionalNotifications: user.user_contact.preferences.promotional_notification !== null,
        generalNotifications: user.user_contact.preferences.general_notification !== null,
        notes: user.user_notes ? user.user_notes.notes_text : null,
        primaryContactMethod: user.user_contact.preferences.system_notification,
        isAdmin: user.user_security.is_admin
    })

    const loadData = async () => {
        try {
            const userId = getUserId()
            const params = new URLSearchParams(location.search);
            const loginId = params.get("u");
            if (!loginId) {
                history.replace('/admin/users');
                return;
            }
            userLoginId.current = loginId;
            const user = await getUserByLoginIdForAdmin(loginId, userId);
            const fields = prepareFields({ ...user });
            originalFields.current = fields;
            setFields(fields)
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const isValidForm = () => {
        const foundErrors = [];
        if (!fields.firstName) foundErrors.push('firstName')
        if (!fields.lastName) foundErrors.push('lastName')
        // if (!fields.email || !isValidEmail(fields.email)) foundErrors.push('email')
        if (fields.phone && fields.phone.length < 10) foundErrors.push('phone')

        if (!fields.primaryContactMethod) foundErrors.push('primaryContactMethod')
        else if (fields.primaryContactMethod === 'phone.primary' && (!fields.phone || fields.phone === '')) {
            foundErrors.push('primaryContactMethod')
            foundErrors.push('phone')
        }

        if (isSuperUser) {
            if (fields.accessLevel !== undefined 
                && (isNaN(fields.accessLevel) || Number(fields.accessLevel) > 100 || Number(fields.accessLevel) < 0)) foundErrors.push('accessLevel');
        }

        setErrors(foundErrors)
        return foundErrors.length === 0
    }

    const preparePayload = () => {
        const masks = []
        const maskProps = {}

        if (fields.firstName !== originalFields.current.firstName) { masks.push('first_name'); maskProps.first_name = fields.firstName }
        if (fields.lastName !== originalFields.current.lastName) { masks.push('last_name'); maskProps.last_name = fields.lastName }
        if (fields.phone !== originalFields.current.phone) { masks.push('phone'); maskProps.phone = fields.phone }
        if (fields.notes !== originalFields.current.notes) { masks.push('user_notes'); maskProps.user_notes = fields.notes }

        if (isSuperUser) {
            if (fields.isAdmin !== originalFields.current.isAdmin) { masks.push('is_admin'); maskProps.is_admin = Boolean(fields.isAdmin) }
        }

        // Preferences
        const preferences = { ...user.user_contact.preferences };
        Object.keys(preferences).forEach(key => {
            preferences[key] = fields.primaryContactMethod;
        })
        if (!fields.generalNotifications) preferences.general_notification = null;
        if (!fields.promotionalNotifications) preferences.promotional_notification = null;

        maskProps.preferences = preferences;
        masks.push('preferences');

        return { masks, maskProps }
        
    }

    const handleSave = async () => {

        if (!isValidForm()) return;
        try {
            setIsLoading(true);

            const payload = preparePayload();
            await updateUserInformation(user.user_login_id, user.user_id, getUserId(), payload.masks, payload.maskProps);
            showNotificationBar('success', `${fields.firstName} has been updated successfully!`);
            history.goBack();
        } catch(err) {
            const resp = await err;
            console.error(resp);
            showNotificationBar('error', 'Uh oh! The user information could not be updated at this time.')
        } finally {
            setIsLoading(false)
        }

    }

    const handleFieldChange = (key, value) => {
        const updatedFields = { ...fields };
        updatedFields[key] = value;
        setFields(updatedFields)
    }

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const renderUserInformationSection = () => (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={6}>
                <StyledTextField
                    error={errors.includes('firstName')}
                    value={fields.firstName}
                    label="*First Name"
                    onChange={(e) => handleFieldChange('firstName', e.target.value)}
                />
                </Grid>
            <Grid item xs={12} md={6}>
                <StyledTextField
                    error={errors.includes('lastName')}
                    value={fields.lastName}
                    label="*Last Name"
                    onChange={(e) => handleFieldChange('lastName', e.target.value)}
                />
            </Grid>
        </Grid>
    )

    const renderUserContactSection = () => (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12}>
                <StyledTextField
                    error={errors.includes('email')}
                    value={fields.email}
                    label="*E-Mail"
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    disabled
                    helperText='The primary email cannot be editted.'
                />
                
            </Grid>
            <Grid item xs={12}>
                <StyledTextField
                    error={errors.includes('phone')}
                    value={fields.phone}
                    label="Phone (optional)"
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                />
            </Grid>
        </Grid>
    )

    const renderUserSecuritySection = () => (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12}>
                <StyledSwitch
                    title="Is Admin"
                    description="This enables the user to access admin level actions."
                    onChange={(e) => { handleFieldChange('isAdmin', e.target.checked) }}
                    checked={fields.isAdmin}
                />
                
            </Grid>
        </Grid>
    )

    const renderUserNotificationPreferences = () => (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12}>
                <StyledDropDown
                        label="*Primary Contact Method"
                        error={errors.includes('primaryContactMethod')}
                        value={fields.primaryContactMethod}
                        onChange={(e) => handleFieldChange('primaryContactMethod', e.target.value)}
                        sx={{ minWidth: '250px'}}
                    >
                        <MenuItem
                            key={'email.primary'}
                            value={'email.primary'}
                        >
                            Email
                        </MenuItem>
                        <MenuItem
                            key={'phone.primary'}
                            value={'phone.primary'}
                        >
                            Phone
                        </MenuItem>
                </StyledDropDown>
            </Grid>
            <Grid item xs={12}>
                 <StyledSwitch 
                    title="Send Promotional Notifications"
                    onChange={(e) => { handleFieldChange('promotionalNotifications', e.target.checked) }}
                    checked={fields.promotionalNotifications}
                />
            </Grid>
            <Grid item xs={12}>
                 <StyledSwitch 
                    title="Send General Notifications"
                    onChange={(e) => { handleFieldChange('generalNotifications', e.target.checked) }}
                    checked={fields.generalNotifications}
                />
            </Grid>
        </Grid>
    )

    const renderUserNotesSection = () => (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12}>
                <StyledMultilineTextField
                label="User Notes"
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                error={errors.includes('notes')}
                value={fields.notes}
              />
            </Grid>
        </Grid>
    )

    return (
        <StyledPage>
            <StyledSubHeader title="Edit User Account" closeIcon />
            {isLoading ? renderLoadingContent() :
                <Paper variant="outlined" sx={{ padding: '16px', marginTop: '20px'}}>
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Personal Information</h4>
                    <p>Provide the first name and last name of the user.</p>
                    {renderUserInformationSection()}
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Contact Information</h4>
                    <p>Provide the contact information for the user</p>
                    {renderUserContactSection()}
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Notification Preferences</h4>
                    <p>Choose the preferences for contacting the user.</p>
                    {renderUserNotificationPreferences()}
                    {isSuperUser &&
                    <>
                        <h4 style={{ margin: 0, fontSize: '24px' }}>User Security</h4>
                        <p>Choose the preferences for contacting the user.</p>
                        {renderUserSecuritySection()}
                    </>}
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Additional Information</h4>
                    <p>Provide any information that should be retained about the client.</p>
                    {renderUserNotesSection()}
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <TextButton onClick={() => history.goBack() } sx={{ marginRight: '10px' }}>
                            Cancel
                        </TextButton>
                        <LoadingButton
                            isLoading={isLoading}
                            disabled={isEqual(fields, originalFields.current)}
                            onClick={handleSave} color="primary">Save</LoadingButton>
                    </Box>
                    
                </Paper>
            }      
     </StyledPage>
    )
}

export default withRouter(observer(AdminEditUserAccountPage));